import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const QuickPage = () => {
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [inputData, setInputData] = useState([]);
  const [domains, setDomains] = useState([]);
  const [whoItems, setWhoItems] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 1000 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setInputData(allItems.filter(item => item.type === 'quick_input'));
        setDomains(allItems.filter(item => item.type === 'quick_domain'));
        setWhoItems(allItems.filter(item => item.type === 'quick_who'));
        setComparisonData(allItems.filter(item => item.type === 'quick_comparison'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <div>
      <Helmet>
        <title>PredyxIQ Quick - My Website</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 70 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "60px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-3" style={{ fontSize: "25px" }}>
            {parse(cms[70]?.cms_page_heading || "Healthcare has traditionally answered one question:<br /><span>\"What disease do I have?\"</span>")}
          </h2>
          {parse(cms[70]?.cms_page_description || "")}
          <a href="#" className="action-items-primary-btn mr-2">
            {cms[70]?.cms_page_small_description || "Start Your Assessment"}
            <i className="fas fa-chevron-right" />
          </a>
         
        </div>
      </section>

      {/* ============================================ */}
      {/* LESS THAN A MINUTE - CMS ID 71 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[71]?.cms_page_heading || "Less Than <span>a Minute.</span>")}
            </h2>
            <h4 className="predyx-sub-title mt-2">
              {cms[71]?.cms_page_small_description || "Years of clinical science behind every result"}
            </h4>
          </div>

          <div className="predyx-content-box">
            <img
              src={`${apiBaseUrl}/assets/images/${cms[71]?.cms_image || 'quick.png'}`}
              alt="About us"
              className="img-fluid"
              style={{ marginBottom: "20px", borderRadius: "10px" }}
            />
            {parse(cms[71]?.cms_page_description || "")}
          </div>

          <div className="predyx-process-section mt-5">
            <h3 className="predyx-process-title text-center">
              {cms[72]?.cms_page_heading || "What does Predyxiq IQ Quick Evaluates?"}
            </h3>
            <p className="predyx-process-subtitle text-center">
              {cms[72]?.cms_page_small_description || "Unlike conventional risk calculators that focus on a single disease, Predyxiq IQ Quick evaluates health across multiple interconnected physiological domains. Each domain contributes to an integrated understanding of the individual's overall biological trajectory."}
            </p>

            <div className="row">
              {domains.map((item, index) => (
                <div className="col-md-3 mb-4" key={index}>
                  <div className="predyx-step-card">
                    <div className="predyx-step-content">
                      <h5>{item.title}</h5>
                      <p>{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="predyx-quote-section mt-3">
            <blockquote>
              {cms[73]?.cms_page_heading || "“Health is never one organ. Health is the interaction between them all.”"}
            </blockquote>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW IT WORKS - CMS ID 74 + Items */}
      {/* ============================================ */}
      <section className="predyx2-section">
        <div className="container">
          <div className="predyx2-heading text-center mb-5">
            <h2 className="predyx2-title mt-2">
              {parse(cms[74]?.cms_page_heading || "How it <span>Works</span>")}
            </h2>
            <h4 className="predyx2-subtitle mt-2">
              {cms[74]?.cms_page_small_description || "Simple for the individual. Sophisticated underneath."}
            </h4>
          </div>

          <div className="row">
            {inputData.map((item, index) => (
              <div className="col-lg-6 mb-4" key={index}>
                <div className="predyx2-card">
                  <div className="predyx2-card-number">0{index + 1}</div>
                  <div className="predyx2-card-content">
                    <h5>{item.title}</h5>
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* YOUR REPORT - CMS ID 75 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[75]?.cms_page_heading || "Your <span>Report</span>")}
            </h2>
            <h4 className="predyx-sub-title mt-2">
              {cms[75]?.cms_page_small_description || "Beautifully designed. Scientifically rigorous. Easy to understand."}
            </h4>
          </div>

          <div className="predyx-content-box">
            {parse(cms[75]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* BUILT FOR PHYSICIANS - CMS ID 76 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#fff" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[76]?.cms_page_heading || "Built for Physicians. <span>Accessible to Everyone.</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[76]?.cms_page_small_description || "Predyxiq IQ Quick is designed to strengthen—not replace—the relationship between individuals and their physicians."}
            </p>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[76]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* QUICK TO CONTINUOUS - CMS ID 77 */}
      {/* ============================================ */}
      <section className="predyx3-section">
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[77]?.cms_page_heading || "From Quick Assessment to <span>Continuous Health Intelligence</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[77]?.cms_page_small_description || "Predyxiq IQ Quick is not the destination. It is the beginning."}
            </p>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[77]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHO IS IT FOR - CMS ID 78 + Items */}
      {/* ============================================ */}
      <section className="explain-section predyx-section" style={{ background: "#f9fcff" }}>
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[78]?.cms_page_heading || "Who is <span>It For?</span>")}
            </h2>
          </div>

          <div className="row">
            {whoItems.map((item, index) => (
              <div className="col-md-4 mb-4" key={index}>
                <div className="predyx-step-card">
                  <div className="predyx-step-content">
                    <h5>{item.title}</h5>
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHY IS DIFFERENT - CMS ID 79 + Comparison */}
      {/* ============================================ */}
      <section className="predyx8-section">
        <div className="container">
          <div className="predyx8-header text-center mb-4">
            <h2 className="predyx8-title">
              {parse(cms[79]?.cms_page_heading || "Why PredyxIQ Quick <span>is Different</span>")}
            </h2>
          </div>

          <div className="table-responsive predyx8-table-wrapper">
            <table className="table predyx8-table mb-0">
              <thead>
                <tr>
                  <th>Conventional Health Risk Calculators</th>
                  <th>PredyxIQ Quick</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.title}</td>
                    <td className="predyx8-highlight">{item.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 80 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "35px", color: "#fff", fontWeight: "600" }}>
              {parse(cms[80]?.cms_page_heading || "Clinical Intelligence should never replace the physician.<br />It should help physicians see further.")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {cms[80]?.cms_page_small_description || "Powered by a proprietary deterministic Clinical Intelligence Algorithm, Predyxiq transforms fragmented healthcare information into transparent, explainable, and reproducible clinical intelligence while keeping the physician at the centre of every clinical decision."}
            </p>
      
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuickPage;